import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../engine/api/context';
import { useUpdate } from '../../engine/api/hooks';
import { ApiError } from '../../engine/api/client';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Textarea } from '../../engine/design/primitives/Input';
import { Badge, Banner, Skeleton } from '../../engine/design/primitives/Misc';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { frameDocument } from './MessageView';
import { useDebounced } from '../../engine/hooks/useDebounced';
import type { ScreenContext } from '../../engine/screens/types';

// Email template detail → "Editor" tab: subject / HTML / text drafts with a live preview
// rendered by the API from the template's vars_schema samples (one template engine, no
// client-side copy), the variables the template may use, and Save. Reset to the system
// default is the resource's own `reset` action, which the detail header renders with its confirm.

interface Preview {
  subject: string;
  html: string;
  text: string;
  missing: string[];
  missingRequired: string[];
}
interface VarSpec {
  type?: string;
  required?: boolean;
  sample?: unknown;
  description?: string;
}

export function TemplateEditor({ id, row, refresh }: ScreenContext) {
  const api = useApi();
  const r = useMemo(() => (row ?? {}) as Record<string, unknown>, [row]);
  const [draft, setDraft] = useState({ subject: String(r['subject'] ?? ''), html: String(r['html'] ?? ''), text: String(r['text'] ?? '') });
  const [pane, setPane] = useState<'html' | 'text'>('html');
  // Re-seed the draft when the row changes underneath (save, reset).
  useEffect(() => {
    setDraft({ subject: String(r['subject'] ?? ''), html: String(r['html'] ?? ''), text: String(r['text'] ?? '') });
  }, [r['subject'], r['html'], r['text']]); // eslint-disable-line react-hooks/exhaustive-deps
  const debounced = useDebounced(draft, 400);
  const dirty = draft.subject !== String(r['subject'] ?? '') || draft.html !== String(r['html'] ?? '') || draft.text !== String(r['text'] ?? '');

  const preview = useQuery({
    queryKey: ['email-template-preview', id, debounced],
    queryFn: () => api.resource('email-templates').action<Preview>('preview', { id, body: { subject: debounced.subject, html: debounced.html, text: debounced.text } }),
    enabled: Boolean(id),
    retry: false,
    placeholderData: (prev) => prev,
  });
  const update = useUpdate('email-templates', { toast: { success: 'Template saved' }, onSuccess: () => refresh() });

  const vars = useMemo(() => Object.entries((r['vars_schema'] as Record<string, VarSpec> | undefined) ?? {}), [r]);
  const previewError = preview.error instanceof ApiError ? preview.error : null;
  const fieldErrors = previewError?.fieldErrors ?? {};
  const doc = useMemo(() => frameDocument(preview.data?.html ?? '', false), [preview.data?.html]);

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-5)' }}>
      {vars.length > 0 && (
        <div className="hf-row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }} aria-label="Variables">
          {vars.map(([k, spec]) => (
            <Badge key={k} tone={spec.required ? 'accent' : 'muted'} dot={false} title={spec.description ?? (spec.sample !== undefined ? `sample: ${JSON.stringify(spec.sample)}` : undefined)}>
              {`{{${k}}}`}
            </Badge>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--s-6)' }}>
        <form
          className="hf-stack"
          aria-label="Template source"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ id: id!, body: draft, previous: { subject: r['subject'], html: r['html'], text: r['text'] } });
          }}
        >
          <Field label="Subject" error={fieldErrors['subject']?.[0]}>
            <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} />
          </Field>
          <Field label="HTML" error={fieldErrors['html']?.[0]}>
            <Textarea value={draft.html} onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))} rows={14} spellCheck={false} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-12)' }} />
          </Field>
          <Field label="Text" help="Optional; derived from the HTML when empty." error={fieldErrors['text']?.[0]}>
            <Textarea value={draft.text} onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))} rows={6} spellCheck={false} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-12)' }} />
          </Field>
          <div className="hf-form-actions">
            {r['is_system'] ? <Badge tone="muted">System template — Reset to default lives in the header</Badge> : null}
            <Button type="submit" variant="primary" busy={update.isPending} disabled={!dirty}>
              Save
            </Button>
          </div>
        </form>

        <section className="hf-stack" aria-label="Preview">
          <div className="hf-row" style={{ justifyContent: 'space-between' }}>
            <div className="hf-caps">Preview</div>
            <Tabs variant="pills" aria-label="Preview part" value={pane} onChange={(k) => setPane(k as 'html' | 'text')} tabs={[{ key: 'html', label: 'HTML' }, { key: 'text', label: 'Text' }]} />
          </div>
          {previewError && <Banner tone="error">{previewError.detail}</Banner>}
          {preview.isLoading ? (
            <Skeleton lines={8} />
          ) : preview.data ? (
            <>
              <div className="hf-card pad" style={{ fontWeight: 600 }} aria-label="Preview subject">
                {preview.data.subject || <span className="hf-muted">(empty subject)</span>}
              </div>
              {pane === 'html' ? (
                <iframe title="Template preview" sandbox="" referrerPolicy="no-referrer" srcDoc={doc} className="hf-card" style={{ width: '100%', minHeight: 360, border: '1px solid var(--rule)', background: '#fff' }} />
              ) : (
                <pre className="hf-card pad" style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--t-13)' }}>
                  {preview.data.text}
                </pre>
              )}
              {preview.data.missing.length > 0 && (
                <p className="hf-muted" style={{ fontSize: 'var(--t-12)', margin: 0 }}>
                  Not in the samples: {preview.data.missing.join(', ')}
                </p>
              )}
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
